import { ReactNode, useMemo, useState } from 'react'
import { Box, Button, Chip, Grid, Typography } from '@mui/material'
import { MarketplaceThemeType } from '../../utils/sharedTypes'
import SectionHeading from './SectionHeading'
import {
  ALL_FUNDERS_FILTER,
  SECTION_PREVIEW_COUNT,
  buildFeaturedThemes,
  buildFunderFilters,
  buildSections,
  filterThemesByFunder,
} from './marketplaceCatalog'

type CatalogSectionsPropsType = {
  themes: MarketplaceThemeType[]
  renderThemeCard: (theme: MarketplaceThemeType, featured: boolean) => ReactNode
  /** Thèmes mis en avant imposés par le lien d'entrée. Vide : la mise en avant est calculée. */
  featuredThemeCodes?: string[]
  /** Inséré entre les puces de financeur et les sections : sert l'échappatoire du parcours à froid. */
  banner?: ReactNode
  /** Message de catalogue vide, quand la page a mieux à dire que le libellé par défaut. */
  emptyState?: ReactNode
}

/** Corps de la marketplace : puces de financeur, mise en avant, sections par financeur.
 *
 * Le filtre par financeur et le dépliage des sections sont un état d'affichage local, sans effet sur
 * l'URL ni sur le panier : ils vivent donc ici plutôt que dans chaque page. */
const CatalogSections = ({
  themes,
  renderThemeCard,
  featuredThemeCodes,
  banner,
  emptyState,
}: CatalogSectionsPropsType) => {
  const [funderFilter, setFunderFilter] = useState<string>(ALL_FUNDERS_FILTER)
  const [expandedSections, setExpandedSections] = useState<string[]>([])

  const funderFilters = useMemo(() => buildFunderFilters(themes), [themes])
  const visibleThemes = useMemo(() => filterThemesByFunder(themes, funderFilter), [themes, funderFilter])

  // La mise en avant n'a de sens qu'en vue d'ensemble : filtrée sur un financeur, on montre
  // directement toutes les formations concernées.
  const showFeaturedSection = funderFilter === ALL_FUNDERS_FILTER
  const featuredThemes = useMemo(() => {
    if (!showFeaturedSection) return []
    if (featuredThemeCodes && featuredThemeCodes.length > 0) {
      return featuredThemeCodes
        .map((code) => visibleThemes.find((theme) => theme.theme_code === code))
        .filter((theme): theme is MarketplaceThemeType => Boolean(theme))
    }
    return buildFeaturedThemes(visibleThemes)
  }, [showFeaturedSection, featuredThemeCodes, visibleThemes])

  const sections = useMemo(() => {
    const featuredCodes = new Set(featuredThemes.map((theme) => theme.theme_code))
    return buildSections(visibleThemes.filter((theme) => !featuredCodes.has(theme.theme_code)))
  }, [visibleThemes, featuredThemes])

  const handleFilterChange = (key: string) => {
    setFunderFilter(key)
    setExpandedSections([])
  }

  return (
    <>
      {funderFilters.length > 1 && (
        <Grid container spacing={1} sx={{ mb: 4 }} alignItems="center">
          {[{ key: ALL_FUNDERS_FILTER, label: 'Tous les financements' }, ...funderFilters].map((filter) => {
            const selected = funderFilter === filter.key
            return (
              <Grid item key={filter.key}>
                <Chip
                  label={filter.label}
                  color={selected ? 'primary' : 'default'}
                  onClick={() => handleFilterChange(filter.key)}
                  // Le fond de page est en blanc cassé : les puces non sélectionnées doivent être
                  // blanches pour rester visibles.
                  sx={
                    selected ? undefined : { backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }
                  }
                />
              </Grid>
            )
          })}
        </Grid>
      )}

      {banner}

      {featuredThemes.length > 0 && (
        <Box sx={{ mb: 7 }}>
          <SectionHeading
            eyebrow="Sélection"
            title="Les plus suivies"
            subtitle="Les formations les mieux notées par vos confrères"
          />
          <Grid container spacing={3}>
            {featuredThemes.map((theme) => (
              <Grid item xs={12} sm={6} md={4} key={theme.theme_code}>
                {renderThemeCard(theme, true)}
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {sections.map((section) => {
        const expanded = expandedSections.includes(section.key)
        const shownThemes = expanded ? section.themes : section.themes.slice(0, SECTION_PREVIEW_COUNT)
        const hiddenCount = section.themes.length - shownThemes.length

        return (
          <Box sx={{ mb: 7 }} key={section.key}>
            <SectionHeading
              eyebrow={section.funderLabel ? `Financement ${section.funderLabel}` : 'Catalogue'}
              title={
                section.funderLabel ? `Finançable avec votre ${section.funderLabel}` : 'Autres formations disponibles'
              }
              subtitle={
                section.budgetLabel
                  ? `Votre enveloppe théorique : ${section.budgetLabel}`
                  : 'Formations éligibles à vos financements'
              }
            />
            <Grid container spacing={3}>
              {shownThemes.map((theme) => (
                <Grid item xs={12} sm={6} md={4} key={theme.theme_code}>
                  {renderThemeCard(theme, false)}
                </Grid>
              ))}
            </Grid>
            {hiddenCount > 0 && (
              <Grid container justifyContent="center" sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={() => setExpandedSections((current) => [...current, section.key])}>
                  {`Voir les ${hiddenCount} autres formations`}
                </Button>
              </Grid>
            )}
          </Box>
        )
      })}

      {visibleThemes.length === 0 &&
        (emptyState || (
          <Typography variant="body2" align="center" sx={{ color: 'text.disabled' }}>
            Aucune formation disponible pour le moment.
          </Typography>
        ))}
    </>
  )
}

export default CatalogSections
